/**
 * My Data Page (Phase 12d-3 — Vault Documents only).
 *
 * Holder-aware data management. The HolderSwitcher at the top lets
 * users toggle between their Personal Holder and any active Org
 * Holder workspace. Both render the Vault Documents view directly —
 * the legacy "Storage" tab that wrapped `user_files` was removed in
 * Phase 12d-3; legacy uploads are migrated/dropped in Phase 13's
 * teardown migration.
 *
 * URL keeps `/my-data`; the selected holder mirrors into a
 * `?holder=<id>` query param for shareability (mirrors Fias 360's
 * convention).
 */

import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { DocumentsListPage } from '@/arche/arches/vault-documents/pages/DocumentsListPage';
import { HolderSwitcher, useHolderSwitcher } from '@/components/HolderSwitcher';

export function MyDataPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Holder switcher — defaults to Personal on first load; mirrors
  // the active holder into ?holder=<id> for shareability.
  // includeGuilds: documents are guild-enabled in V1, so guild shared data
  // spaces are selectable here.
  const switcher = useHolderSwitcher({ defaultToPersonal: true, includeGuilds: true });
  const urlHolder = searchParams.get('holder');
  useEffect(() => {
    if (urlHolder && urlHolder !== switcher.selectedHolderId) {
      switcher.setSelectedHolderId(urlHolder);
    }
  }, [urlHolder]);
  useEffect(() => {
    if (!switcher.selectedHolderId) return;
    if (searchParams.get('holder') === switcher.selectedHolderId) return;
    const next = new URLSearchParams(searchParams);
    next.set('holder', switcher.selectedHolderId);
    setSearchParams(next, { replace: true });
  }, [switcher.selectedHolderId]);

  // Optional deep link into a specific folder. Path is the stable
  // identifier (Holder-independent + shareable); DocumentsListPage
  // resolves it to a folderId from the already-loaded folders query.
  const initialFolderPath = searchParams.get('folderPath') ?? undefined;

  return (
    <div>
      <div className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-6 lg:px-8">
          <div>
            <h1 className="text-2xl font-bold text-foreground">My Data</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {switcher.selectedOption?.holderType === 'org'
                ? `Workspace documents for ${switcher.selectedOption.displayName}`
                : switcher.selectedOption?.holderType === 'guild'
                  ? `Shared guild documents for ${switcher.selectedOption.displayName}`
                  : 'Your personal documents and AI-generated content'}
            </p>
          </div>
          <HolderSwitcher
            selectedHolderId={switcher.selectedHolderId}
            onChange={(next) => switcher.setSelectedHolderId(next)}
            includeGuilds
          />
        </div>
      </div>

      <div className="mx-auto max-w-6xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
        {!switcher.selectedHolderId ? (
          <Card>
            <CardContent className="py-12 text-center text-sm text-muted-foreground">
              Choose a workspace above to see your documents.
            </CardContent>
          </Card>
        ) : (
          <DocumentsListPage
            holderId={switcher.selectedHolderId}
            holder={switcher.selectedOption}
            initialFolderPath={initialFolderPath}
            embedded
          />
        )}
      </div>
    </div>
  );
}

export default MyDataPage;
